import React, { useState } from 'react';
import { Download, Loader2, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from './auth/AuthProvider';
import { CSVExporter } from '../utils/csvExporter';

interface DataExporterProps {
  className?: string;
}

export const DataExporter: React.FC<DataExporterProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleExportUserProfiles = async () => {
    if (!user) {
      setExportStatus({
        type: 'error',
        message: 'You must be logged in to export data'
      });
      return;
    }

    setIsExporting(true);
    setExportStatus({ type: null, message: '' });

    try {
      // Check if user has admin privileges or export their own data only
      const isAdmin = user.email?.includes('admin') || user.email?.includes('safeplayai');
      
      let query = supabase.from('user_profiles').select('*');
      
      // If not admin, only export current user's profile
      if (!isAdmin) {
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        setExportStatus({
          type: 'error',
          message: 'No user profile data found to export'
        });
        return;
      }

      // Format data for better readability
      const formattedData = data.map(profile => ({
        user_id: profile.user_id,
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        country: profile.country || '',
        created_at: profile.created_at,
        updated_at: profile.updated_at
      }));

      // Generate CSV content
      const csvContent = CSVExporter.arrayToCSV(formattedData, {
        includeHeaders: true,
        dateFormat: 'locale'
      });

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = isAdmin 
        ? `user_profiles_export_${timestamp}.csv`
        : `my_profile_export_${timestamp}.csv`;

      // Download the file
      CSVExporter.downloadCSV(csvContent, filename);

      setExportStatus({
        type: 'success',
        message: `Successfully exported ${data.length} profile record${data.length > 1 ? 's' : ''} to ${filename}`
      });

    } catch (error) {
      console.error('Export error:', error);
      setExportStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to export user profiles'
      });
    } finally {
      setIsExporting(false);
      
      // Clear status message after 5 seconds
      setTimeout(() => {
        setExportStatus({ type: null, message: '' });
      }, 5000);
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
          <FileText className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Data Export</h3>
      </div>

      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Export User Profiles</h4>
          <p className="text-sm text-gray-600 mb-4">
            Download user profile data as a CSV file. {user?.email?.includes('admin') || user?.email?.includes('safeplayai') 
              ? 'As an admin, you can export all user profiles.' 
              : 'You can export your own profile data.'}
          </p>
          
          <button
            onClick={handleExportUserProfiles}
            disabled={isExporting || !user}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Export User Profiles</span>
              </>
            )}
          </button>
        </div>

        {/* Status Messages */}
        {exportStatus.type && (
          <div className={`p-4 rounded-lg border ${
            exportStatus.type === 'success' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center space-x-2">
              {exportStatus.type === 'success' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <AlertCircle className="w-4 h-4 text-red-600" />
              )}
              <span className={`text-sm font-medium ${
                exportStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {exportStatus.type === 'success' ? 'Export Successful' : 'Export Failed'}
              </span>
            </div>
            <p className={`text-sm mt-1 ${
              exportStatus.type === 'success' ? 'text-green-700' : 'text-red-700'
            }`}>
              {exportStatus.message}
            </p>
          </div>
        )}

        {/* Export Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h5 className="font-semibold text-gray-900 mb-2">Export Details</h5>
          <div className="text-sm text-gray-600 space-y-1">
            <p>• File format: CSV (Comma Separated Values)</p>
            <p>• Includes all profile fields and timestamps</p>
            <p>• Compatible with Excel, Google Sheets, and other spreadsheet applications</p>
            <p>• Data is exported with proper escaping for special characters</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExporter;